import { shippingClassFor, shippingUnitLabel, installmentProvidersFor, HOME_NETWORK_LISTING_PART, } from '@evi/shared';
/**
 * Store catalog engine (store-kit). Extracted from apps/web's store pages so
 * the in-app driver store (apps/web) and the public shop (apps/shop) fold the
 * same flat SKU list into the same product families with the same pricing +
 * shipping copy. Pure data logic — no React, no auth/cart store — keyed on the
 * SAME @evi/shared policy the server charges from, so the buyer is never told a
 * shipping rule the checkout doesn't apply.
 */
/** One-line, per-unit shipping copy for a product, by shipping class: each
 *  charger class carries its own per-unit rate ("$15 shipping" through
 *  "$499 freight per unit") and accessories read "Free shipping". Derived
 *  from the shared policy so the copy and the charge never drift. Takes
 *  optional tier (cart items snapshot it; catalog products always have it). */
export function shippingLineFor(p) {
    return shippingUnitLabel(shippingClassFor({ part_number: p.part_number, name: p.name, tier: p.tier ?? '' }));
}
/** One-line installments copy. Afterpay may be named at or under its US
 *  cap; above it, Affirm only. */
export function installmentLineFor(priceCents) {
    return installmentProvidersFor(priceCents) === 'both'
        ? 'Pay in installments with Afterpay or Affirm at checkout.'
        : 'Pay in installments with Affirm at checkout.';
}
// ── Committed media (2026 hero refresh + AC film batch). Each family carries
// its OWN hero still (webp); the click-to-play film + its loading poster stay
// shared per hardware family. The wallbox film is the Home Hero v2 footage
// (one file, three family references); AC Ultra's is its own product shot
// under pedestal/. Both films exist in BOTH apps' media copies — the shop's
// were missing entirely (the "only DC plays" drift), fixed in the AC film
// batch. ─────────────────────────────────────────────────────────────────────
const WALLBOX_FILM = {
    film: '/media/products/wallbox/film.mp4',
    poster: '/media/products/wallbox/film-poster.jpg',
};
const ELITE_HOME = {
    hero: '/media/products/wallbox/hero-elite-home.webp',
    ...WALLBOX_FILM,
};
const ELITE_COMMERCIAL = {
    hero: '/media/products/wallbox/hero-elite-commercial.webp',
    ...WALLBOX_FILM,
};
const ACPRO = {
    hero: '/media/products/wallbox/hero-acpro.webp',
    ...WALLBOX_FILM,
};
const PEDESTAL = {
    hero: '/media/products/pedestal/hero.webp',
    film: '/media/products/pedestal/film.mp4',
    poster: '/media/products/pedestal/film-poster.jpg',
};
const CABLE = { hero: '/media/products/accessories/cable.jpg' };
// DC cinematic media (public shop). hero-loop.mp4 is the shared looping film;
// each DC family now has its own hero still (DC Compact in its own folder).
const DC_FILM = {
    film: '/media/products/dcfc/hero-loop.mp4',
    poster: '/media/products/dcfc/hero-loop-poster.jpg',
};
// DC Compact has its OWN click-to-play film (2026 media batch) + its loading
// poster; the other DC families keep the shared DC_FILM (hero-loop.mp4).
const DC_COMPACT = {
    hero: '/media/products/dc_compact/hero.webp',
    film: '/media/products/dc_compact/film.mp4',
    poster: '/media/products/dc_compact/film-poster.jpg',
};
const DC_CCS1 = { hero: '/media/products/dcfc/hero.webp', ...DC_FILM };
const DC_BOOST = { hero: '/media/products/dcfc/hero-boost.webp', ...DC_FILM };
const FAMILY_DEFS = [
    // "Sell it under Home" (Warren, 2026-10-02): the network-ready AC Elite
    // Commercial listed as a home product (its own part number, the same Autel
    // MCC50AHI; see @evi/shared storeListings.ts). It must come before the AC
    // Elite Home def, which claims every other tier 'home' SKU, and it leads
    // the Home tier so a host sees the model that joins the network first.
    {
        key: 'ac-elite-commercial-home',
        title: 'AC Elite Commercial',
        blurb: 'Network-ready 12 kW Level 2 home charger from Autel. Connects to the EV Initiative network over your home Wi-Fi.',
        media: ELITE_COMMERCIAL,
        match: (p) => p.part_number === HOME_NETWORK_LISTING_PART,
    },
    {
        key: 'ac-elite-home',
        title: 'AC Elite Home',
        blurb: '9.6 kW Level 2 home EV charger from Autel (12 kW hardwired). Does not connect to the EV Initiative network.',
        media: ELITE_HOME,
        match: (p) => p.tier === 'home',
    },
    {
        key: 'ac-elite-commercial',
        title: 'AC Elite Commercial',
        blurb: '12 kW Level 2 commercial EV charger from Autel.',
        media: ELITE_COMMERCIAL,
        match: (p) => p.part_number === 'MCC50AHI',
    },
    {
        key: 'ac-pro-commercial',
        title: 'AC Pro Commercial',
        blurb: '19.2 kW Level 2 commercial EV charger from Autel.',
        media: ACPRO,
        match: (p) => p.part_number === 'UW19C002',
    },
    {
        key: 'ac-ultra',
        title: 'AC Ultra',
        blurb: '19.2 kW dual-port Level 2 commercial EV charger from Autel. One unit serves two stalls.',
        media: PEDESTAL,
        match: (p) => /^AC Ultra/i.test(p.name),
    },
    // ── DC (public shop only) ────────────────────────────────────────────────
    // DC products are surfaces:['public'], so they never appear in the in-app
    // catalog (IN_APP_FILTER) — these defs are inert for apps/web and only fire
    // on the public shop's catalog.
    {
        key: 'dc-compact',
        title: 'DC Compact',
        blurb: '40 kW dual-CCS DC fast charger, wall or pedestal. Includes pre-commissioning to the network.',
        media: DC_COMPACT,
        match: (p) => /^DC40A[WP]N25$/.test(p.part_number),
    },
    {
        key: 'maxicharger-dc-ccs1',
        // Displays as "MaxiCharger DC Fast" everywhere (cards + detail h1). The
        // connector is chosen after purchase (CCS1 or NACS), so the name no longer
        // pins CCS1. Family key + part numbers unchanged.
        title: 'MaxiCharger DC Fast',
        blurb: '60 to 120 kW DC fast charging. Includes pre-commissioning to the network.',
        media: DC_CCS1,
        match: (p) => /^UF\d{3}A3001$/.test(p.part_number),
    },
    {
        key: 'maxicharger-dc-boost',
        title: 'MaxiCharger DC Fast Boost',
        blurb: '60 to 240 kW DC fast charging, Boost liquid-cooled cable. Includes pre-commissioning to the network.',
        media: DC_BOOST,
        match: (p) => /^UF\d{3}A4001$/.test(p.part_number),
    },
];
export const FAMILY_MANUALS = {
    'ac-elite-commercial-home': { path: '/media/products/wallbox/manual.pdf', leadSource: 'spec_sheet_wallbox' },
    'ac-elite-home': { path: '/media/products/wallbox/manual.pdf', leadSource: 'spec_sheet_wallbox' },
    'ac-elite-commercial': { path: '/media/products/wallbox/manual.pdf', leadSource: 'spec_sheet_wallbox' },
    // AC Pro Commercial ships the same Autel wallbox full manual (the deployed
    // wallbox/manual.pdf IS "EVI Autel Wallbox - Full Manual.pdf", md5-identical
    // to the source file), so it joins the same lead source + Turnstile flow.
    'ac-pro-commercial': { path: '/media/products/wallbox/manual.pdf', leadSource: 'spec_sheet_wallbox' },
    'ac-ultra': { path: '/media/products/pedestal/manual.pdf', leadSource: 'spec_sheet_pedestal' },
    // DC (public shop). DC Compact has its own manual; MaxiCharger DC Fast and DC
    // Fast Boost share one manual (two family keys, one PDF path).
    'dc-compact': { path: '/media/products/dc_compact/manual.pdf', leadSource: 'spec_sheet_dc_compact' },
    'maxicharger-dc-ccs1': { path: '/media/products/dcfc/manual.pdf', leadSource: 'spec_sheet_dcfc' },
    'maxicharger-dc-boost': { path: '/media/products/dcfc/manual.pdf', leadSource: 'spec_sheet_dcfc' },
};
/** Tier display order + labels, matching the pre-redesign catalog. dc and
 *  high_power never reach the in-app surface, so those groups stay empty. */
export const TIER_ORDER = ['home', 'commercial_ac', 'dc', 'high_power', 'accessory', 'site_connectivity'];
export const TIER_LABEL = {
    home: 'Home',
    commercial_ac: 'Commercial AC',
    dc: 'DC fast',
    high_power: 'High power',
    accessory: 'Accessories',
    // Warren, 21 October 2026. Its products are seeded unpublished, so the group stays empty until he publishes them.
    site_connectivity: 'Site connectivity',
};
function variantLabel(title, product) {
    const stripped = product.name.replace(new RegExp(`^${escapeRegExp(title)}\\s*`, 'i'), '').trim();
    return stripped.length > 0 ? stripped : 'Standard';
}
function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/** Accessory card images by exact part number (seed part_number values).
 *  Same explicit-map pattern the product families use above. */
const ACCESSORY_MEDIA = {
    RFID: { hero: '/media/products/accessories/rfid.jpg' },
    UP10: { hero: '/media/products/accessories/pedestal-post-ac.jpg' },
    // No photo of their own yet (3 November 2026): the closest real one, captioned, because a listing needs an image.
    DCPP: { hero: '/media/products/dc_compact/hero.webp', caption: 'Shown on a DC Compact charger, sold separately.' },
    WMKIT: { hero: '/media/products/dc_compact/hero.webp', caption: 'DC Compact charger shown, sold separately.' },
    US480A2L0: { hero: '/media/products/dcfc/hero-boost.webp', caption: 'One Boost dispenser shown. The set includes 2.' },
    US640A2L0: { hero: '/media/products/dcfc/hero-boost.webp', caption: 'One Boost dispenser shown. The set includes 2.' },
};
/** Media for a standalone SKU that matched no family: exact part-number map
 *  first, then the cable image for any SKU whose name reads as a cable (kept
 *  wired so a future cable SKU lights up), else poster-less. */
function singletonMedia(product) {
    return ACCESSORY_MEDIA[product.part_number]
        ?? (/cable/i.test(product.name) ? CABLE : null);
}
/**
 * Resolve the family a single product belongs to. Used by the deep-link
 * product page so it renders the same title, media, and sibling variants as
 * the catalog card the user would have tapped.
 */
export function familyForProduct(product, catalog) {
    const families = buildFamilies(catalog);
    const hit = families.find((f) => f.variants.some((v) => v.product.id === product.id));
    if (hit)
        return hit;
    // Product not in the passed catalog (stale link, or catalog not loaded):
    // build a lone family from the product itself.
    return buildFamilies([product])[0];
}
/** Fold a flat SKU list into families, sorted variants, "from" price. */
export function buildFamilies(products) {
    const byKey = new Map();
    const order = [];
    for (const p of products) {
        const def = FAMILY_DEFS.find((d) => d.match(p));
        const key = def ? def.key : `sku-${p.part_number}`;
        if (!byKey.has(key)) {
            order.push(key);
            byKey.set(key, {
                def: def ?? { key, title: p.name, blurb: '', media: singletonMedia(p) },
                items: [],
            });
        }
        byKey.get(key).items.push(p);
    }
    // Families follow the FAMILY_DEFS table order, so the network-ready Home
    // listing leads the Home tier even though its catalog row was seeded last.
    // SKUs that matched no def follow, in catalog order (sort is stable). For
    // every other tier the table order is the order the seed inserts them.
    const rank = (key) => {
        const i = FAMILY_DEFS.findIndex((d) => d.key === key);
        return i === -1 ? FAMILY_DEFS.length : i;
    };
    order.sort((a, b) => rank(a) - rank(b));
    return order.map((key) => {
        const { def, items } = byKey.get(key);
        const variants = items
            .map((product) => ({ product, label: variantLabel(def.title, product) }))
            // Recommended variant first, then price ascending. The recommended flag
            // drives both the display order and the sheet's Recommended pill.
            .sort((a, b) => {
            const rec = Number(b.product.recommended ?? false) - Number(a.product.recommended ?? false);
            return rec !== 0 ? rec : a.product.price_cents - b.product.price_cents;
        });
        // FROM price stays the family minimum regardless of display order, so the
        // card's "From" logic is unchanged by the recommended-first reorder.
        const fromPriceCents = Math.min(...items.map((p) => p.price_cents));
        return {
            key,
            title: def.title,
            tier: items[0].tier,
            blurb: def.blurb.length > 0 ? def.blurb : null,
            media: def.media,
            variants,
            fromPriceCents,
        };
    });
}
/** SKUs hidden from the in-app catalog client-side. The in-app store shows no
 *  DC hardware, so both DC accessories stay out: DCPP (DC pedestal post) and
 *  WMKIT (DC Compact wall mount bracket kit). Seed and API are unchanged; a
 *  direct /store/product/:id deep link still resolves. The charging-cable
 *  replacement part has no SKU in the seed yet; when one lands it belongs in
 *  this set too (standalone-store only). */
const IN_APP_HIDDEN_PARTS = new Set(['DCPP', 'WMKIT']);
/** Families grouped by tier in TIER_ORDER, empty tiers dropped. */
export function groupCatalog(products) {
    const families = buildFamilies(products.filter((p) => !IN_APP_HIDDEN_PARTS.has(p.part_number)));
    return TIER_ORDER
        .map((tier) => ({ tier, families: families.filter((f) => f.tier === tier) }))
        .filter((g) => g.families.length > 0);
}
/** Whether a family spans more than one buyable SKU (drives "Options" vs a
 *  direct add on the card). */
export function hasChoices(family) {
    return family.variants.length > 1;
}
// ── Public shop (evinitiative.shop) presentation ─────────────────────────────
/** Shop tier order + labels. DC leads; high_power is quote-only and not sold
 *  online, so it is absent from the shop's own order (it still has a label for
 *  completeness). */
export const SHOP_TIER_ORDER = ['dc', 'commercial_ac', 'home', 'accessory', 'site_connectivity'];
export const SHOP_TIER_LABEL = {
    dc: 'Commercial DC',
    commercial_ac: 'Commercial AC',
    home: 'Home Charging',
    accessory: 'Accessories',
    high_power: 'High Power',
    site_connectivity: 'Site connectivity',
};
/** Shop category slug <-> tier — the URL scheme for the shop's category pages
 *  (/commercial-dc, /commercial-ac, /home-charging, /accessories). */
export const SHOP_CATEGORIES = [
    { slug: 'commercial-dc', tier: 'dc', label: 'Commercial DC' },
    { slug: 'commercial-ac', tier: 'commercial_ac', label: 'Commercial AC' },
    { slug: 'home-charging', tier: 'home', label: 'Home Charging' },
    { slug: 'accessories', tier: 'accessory', label: 'Accessories' },
];
export function shopTierForSlug(slug) {
    return SHOP_CATEGORIES.find((c) => c.slug === slug)?.tier ?? null;
}
/** Families grouped by tier in SHOP_TIER_ORDER, empty tiers dropped. Unlike the
 *  in-app groupCatalog, this shows DC and does NOT hide the DC accessories
 *  (DCPP / WMKIT) — the public shop sells them. */
export function groupCatalogForShop(products) {
    const families = buildFamilies(products);
    return SHOP_TIER_ORDER
        .map((tier) => ({ tier, families: families.filter((f) => f.tier === tier) }))
        .filter((g) => g.families.length > 0);
}
/**
 * Static image for a cart row, resolved through the SAME family/accessory
 * mapping the catalog cards use (familyForProduct -> media), so cart
 * thumbnails can never drift from card images. Always a still: media.hero
 * is required on every FamilyMedia, so a film-bearing family still yields
 * its hero image here, never video. Returns null when the product is not
 * in the catalog (delisted mid-cart) or its family carries no media; the
 * cart renders such rows without a thumbnail.
 */
export function cartItemImage(productId, catalog) {
    const product = catalog.find((p) => p.id === productId);
    if (!product)
        return null;
    return familyForProduct(product, catalog).media?.hero ?? null;
}
//# sourceMappingURL=catalog.js.map